import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  FileText,
  Palette,
  Layout,
  Type,
  Star,
  Edit,
  Check,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default letterhead template structure
const DEFAULT_TEMPLATE = {
  name: 'New Template',
  description: '',
  is_default: false,
  header: {
    show_logo: true,
    logo_position: 'left',
    logo_width_inches: 2.0,
    show_firm_name: true,
    firm_name_position: 'right',
    show_address: true,
    show_contact: true,
    header_color: '#006666',
    font_family: 'Arial',
    font_size: 10,
    include_rics_badge: true,
    custom_header_text: ''
  },
  footer: {
    style: 'standard',
    show_page_numbers: true,
    page_number_format: 'Page {page} of {total}',
    show_confidentiality: true,
    confidentiality_text: 'PRIVATE & CONFIDENTIAL',
    show_website: true,
    show_email: true,
    show_phone: true,
    show_rics_registration: true,
    footer_color: '#666666',
    font_size: 8,
    custom_footer_text: '',
    disclaimer_text: ''
  },
  colors: {
    primary_color: '#006666',
    secondary_color: '#0EA5E9',
    accent_color: '#7C3AED',
    text_color: '#1a1a1a',
    heading_color: '#006666',
    border_color: '#e5e7eb'
  },
  margin_top: 1.0,
  margin_bottom: 1.0,
  margin_left: 1.0,
  margin_right: 1.0,
  body_font: 'Arial',
  heading_font: 'Arial',
  body_font_size: 11,
  heading_font_size: 14,
  show_header_line: true,
  show_footer_line: true,
  header_line_width: 1.0,
  footer_line_width: 0.5,
  watermark: {
    enabled: false,
    watermark_type: 'text',
    text: 'DRAFT',
    font_size: 72,
    font_family: 'Arial',
    color: '#CCCCCC',
    opacity: 0.3,
    rotation: -45,
    position: 'diagonal',
    apply_to_draft: true,
    apply_to_final: false,
    draft_text: 'DRAFT',
    final_text: ''
  }
};

export default function LetterheadTemplateManager() {
  const { token } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/compliance/letterheads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/compliance/letterheads`, DEFAULT_TEMPLATE, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates([...templates, response.data.template]);
      setSelectedTemplate(response.data.template);
      setEditedTemplate(response.data.template);
      setEditMode(true);
      toast.success('Template created');
    } catch (err) {
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editedTemplate) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/compliance/letterheads/${editedTemplate.id}`,
        editedTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplates(templates.map(t => t.id === editedTemplate.id ? response.data.template : t));
      setSelectedTemplate(response.data.template);
      setEditMode(false);
      toast.success('Template saved');
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/compliance/letterheads/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(templates.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setEditedTemplate(null);
        setEditMode(false);
      }
      toast.success('Template deleted');
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await axios.post(`${API}/compliance/letterheads/${templateId}/set-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(templates.map(t => ({ ...t, is_default: t.id === templateId })));
      toast.success('Default template updated');
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  const updateEditedTemplate = (path, value) => {
    setEditedTemplate(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-accent" />
              Letterhead Templates
            </CardTitle>
            <CardDescription>
              Create and manage letterhead styles for your reports
            </CardDescription>
          </div>
          <Button onClick={handleCreateTemplate} disabled={saving} size="sm" data-testid="create-letterhead-btn">
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No letterhead templates yet</p>
            <p className="text-sm">Create a template to customize your report headers and footers</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {/* Template List */}
            <div className="col-span-1 border-r pr-4">
              <div className="space-y-2">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedTemplate?.id === template.id 
                        ? 'border-accent bg-accent/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setEditedTemplate(template);
                      setEditMode(false);
                    }}
                    data-testid={`letterhead-template-${template.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{template.name}</span>
                      {template.is_default && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    {template.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {template.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Template Editor */}
            <div className="col-span-2 pl-4">
              {selectedTemplate ? (
                <div>
                  {/* Editor Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      {editMode ? (
                        <Input
                          value={editedTemplate?.name || ''}
                          onChange={(e) => updateEditedTemplate('name', e.target.value)}
                          className="font-medium w-48"
                        />
                      ) : (
                        <h3 className="font-medium">{selectedTemplate.name}</h3>
                      )}
                      {selectedTemplate.is_default && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {editMode ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditedTemplate(selectedTemplate);
                            setEditMode(false);
                          }}>
                            <X className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveTemplate} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          {!selectedTemplate.is_default && (
                            <Button size="sm" variant="outline" onClick={() => handleSetDefault(selectedTemplate.id)}>
                              <Star className="h-4 w-4 mr-1" /> Set Default
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteTemplate(selectedTemplate.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Editor Tabs */}
                  <Tabs defaultValue="header" className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="header" className="text-xs">
                        <Layout className="h-3 w-3 mr-1" /> Header
                      </TabsTrigger>
                      <TabsTrigger value="footer" className="text-xs">
                        <Layout className="h-3 w-3 mr-1" /> Footer
                      </TabsTrigger>
                      <TabsTrigger value="watermark" className="text-xs">
                        <Type className="h-3 w-3 mr-1" /> Watermark
                      </TabsTrigger>
                      <TabsTrigger value="colors" className="text-xs">
                        <Palette className="h-3 w-3 mr-1" /> Colors
                      </TabsTrigger>
                      <TabsTrigger value="layout" className="text-xs">
                        <Type className="h-3 w-3 mr-1" /> Layout
                      </TabsTrigger>
                    </TabsList>

                    {/* Header Settings */}
                    <TabsContent value="header" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Logo</Label>
                          <Switch
                            checked={editedTemplate?.header?.show_logo ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('header.show_logo', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Firm Name</Label>
                          <Switch
                            checked={editedTemplate?.header?.show_firm_name ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('header.show_firm_name', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Contact Info</Label>
                          <Switch
                            checked={editedTemplate?.header?.show_contact ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('header.show_contact', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Include RICS Badge</Label>
                          <Switch
                            checked={editedTemplate?.header?.include_rics_badge ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('header.include_rics_badge', v)}
                            disabled={!editMode}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Logo Position</Label>
                          <Select
                            value={editedTemplate?.header?.logo_position || 'left'}
                            onValueChange={(v) => updateEditedTemplate('header.logo_position', v)}
                            disabled={!editMode}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Logo Width (inches)</Label>
                          <Input
                            type="number"
                            step="0.25"
                            min="0.5"
                            max="4"
                            value={editedTemplate?.header?.logo_width_inches || 2}
                            onChange={(e) => updateEditedTemplate('header.logo_width_inches', parseFloat(e.target.value))}
                            disabled={!editMode}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Header Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={editedTemplate?.header?.header_color || '#006666'}
                            onChange={(e) => updateEditedTemplate('header.header_color', e.target.value)}
                            disabled={!editMode}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <Input
                            value={editedTemplate?.header?.header_color || '#006666'}
                            onChange={(e) => updateEditedTemplate('header.header_color', e.target.value)}
                            disabled={!editMode}
                            className="w-28"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    {/* Footer Settings */}
                    <TabsContent value="footer" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Page Numbers</Label>
                          <Switch
                            checked={editedTemplate?.footer?.show_page_numbers ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('footer.show_page_numbers', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Confidentiality</Label>
                          <Switch
                            checked={editedTemplate?.footer?.show_confidentiality ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('footer.show_confidentiality', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show RICS Registration</Label>
                          <Switch
                            checked={editedTemplate?.footer?.show_rics_registration ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('footer.show_rics_registration', v)}
                            disabled={!editMode}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Confidentiality Text</Label>
                        <Input
                          value={editedTemplate?.footer?.confidentiality_text || ''}
                          onChange={(e) => updateEditedTemplate('footer.confidentiality_text', e.target.value)}
                          disabled={!editMode}
                          className="mt-1"
                          placeholder="PRIVATE & CONFIDENTIAL"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Footer Style</Label>
                        <Select
                          value={editedTemplate?.footer?.style || 'standard'}
                          onValueChange={(v) => updateEditedTemplate('footer.style', v)}
                          disabled={!editMode}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minimal">Minimal</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="detailed">Detailed</SelectItem>
                            <SelectItem value="legal">Legal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Disclaimer Text (optional)</Label>
                        <Input
                          value={editedTemplate?.footer?.disclaimer_text || ''}
                          onChange={(e) => updateEditedTemplate('footer.disclaimer_text', e.target.value)}
                          disabled={!editMode}
                          className="mt-1"
                          placeholder="Optional disclaimer text..."
                        />
                      </div>
                    </TabsContent>

                    {/* Watermark Settings */}
                    <TabsContent value="watermark" className="space-y-4 mt-4">
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                        <p className="text-sm text-amber-800">
                          Add diagonal watermarks like "DRAFT" or "CONFIDENTIAL" to your reports. 
                          Watermarks help identify document status and prevent unauthorized use.
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="text-sm font-medium">Enable Watermark</Label>
                          <p className="text-xs text-muted-foreground">Show watermark on reports</p>
                        </div>
                        <Switch
                          checked={editedTemplate?.watermark?.enabled ?? false}
                          onCheckedChange={(v) => updateEditedTemplate('watermark.enabled', v)}
                          disabled={!editMode}
                        />
                      </div>
                      
                      {editedTemplate?.watermark?.enabled && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm">Apply to Draft</Label>
                              <Switch
                                checked={editedTemplate?.watermark?.apply_to_draft ?? true}
                                onCheckedChange={(v) => updateEditedTemplate('watermark.apply_to_draft', v)}
                                disabled={!editMode}
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <Label className="text-sm">Apply to Final</Label>
                              <Switch
                                checked={editedTemplate?.watermark?.apply_to_final ?? false}
                                onCheckedChange={(v) => updateEditedTemplate('watermark.apply_to_final', v)}
                                disabled={!editMode}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm">Draft Text</Label>
                              <Input
                                value={editedTemplate?.watermark?.draft_text || 'DRAFT'}
                                onChange={(e) => updateEditedTemplate('watermark.draft_text', e.target.value)}
                                disabled={!editMode}
                                className="mt-1"
                                placeholder="DRAFT"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Final Text (optional)</Label>
                              <Input
                                value={editedTemplate?.watermark?.final_text || ''}
                                onChange={(e) => updateEditedTemplate('watermark.final_text', e.target.value)}
                                disabled={!editMode}
                                className="mt-1"
                                placeholder="CONFIDENTIAL"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm">Font Size</Label>
                              <Input
                                type="number"
                                min="24"
                                max="120"
                                value={editedTemplate?.watermark?.font_size || 72}
                                onChange={(e) => updateEditedTemplate('watermark.font_size', parseInt(e.target.value))}
                                disabled={!editMode}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Rotation (degrees)</Label>
                              <Select
                                value={String(editedTemplate?.watermark?.rotation || -45)}
                                onValueChange={(v) => updateEditedTemplate('watermark.rotation', parseInt(v))}
                                disabled={!editMode}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="-45">Diagonal (-45°)</SelectItem>
                                  <SelectItem value="-30">Slanted (-30°)</SelectItem>
                                  <SelectItem value="0">Horizontal (0°)</SelectItem>
                                  <SelectItem value="45">Diagonal (45°)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm">Watermark Color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <input
                                  type="color"
                                  value={editedTemplate?.watermark?.color || '#CCCCCC'}
                                  onChange={(e) => updateEditedTemplate('watermark.color', e.target.value)}
                                  disabled={!editMode}
                                  className="w-10 h-10 rounded cursor-pointer"
                                />
                                <Input
                                  value={editedTemplate?.watermark?.color || '#CCCCCC'}
                                  onChange={(e) => updateEditedTemplate('watermark.color', e.target.value)}
                                  disabled={!editMode}
                                  className="w-28"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm">Opacity ({Math.round((editedTemplate?.watermark?.opacity || 0.3) * 100)}%)</Label>
                              <input
                                type="range"
                                min="0.1"
                                max="0.8"
                                step="0.05"
                                value={editedTemplate?.watermark?.opacity || 0.3}
                                onChange={(e) => updateEditedTemplate('watermark.opacity', parseFloat(e.target.value))}
                                disabled={!editMode}
                                className="w-full mt-3"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </TabsContent>

                    {/* Colors Settings */}
                    <TabsContent value="colors" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { key: 'primary_color', label: 'Primary Color', default: '#006666' },
                          { key: 'secondary_color', label: 'Secondary Color', default: '#0EA5E9' },
                          { key: 'accent_color', label: 'Accent Color', default: '#7C3AED' },
                          { key: 'heading_color', label: 'Heading Color', default: '#006666' },
                          { key: 'text_color', label: 'Text Color', default: '#1a1a1a' },
                          { key: 'border_color', label: 'Border Color', default: '#e5e7eb' }
                        ].map(({ key, label, default: defaultVal }) => (
                          <div key={key}>
                            <Label className="text-sm">{label}</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={editedTemplate?.colors?.[key] || defaultVal}
                                onChange={(e) => updateEditedTemplate(`colors.${key}`, e.target.value)}
                                disabled={!editMode}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                              <Input
                                value={editedTemplate?.colors?.[key] || defaultVal}
                                onChange={(e) => updateEditedTemplate(`colors.${key}`, e.target.value)}
                                disabled={!editMode}
                                className="w-24 text-xs"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>

                    {/* Layout Settings */}
                    <TabsContent value="layout" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Header Line</Label>
                          <Switch
                            checked={editedTemplate?.show_header_line ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('show_header_line', v)}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show Footer Line</Label>
                          <Switch
                            checked={editedTemplate?.show_footer_line ?? true}
                            onCheckedChange={(v) => updateEditedTemplate('show_footer_line', v)}
                            disabled={!editMode}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        {[
                          { key: 'margin_top', label: 'Top Margin' },
                          { key: 'margin_bottom', label: 'Bottom Margin' },
                          { key: 'margin_left', label: 'Left Margin' },
                          { key: 'margin_right', label: 'Right Margin' }
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <Label className="text-xs">{label} (in)</Label>
                            <Input
                              type="number"
                              step="0.25"
                              min="0.5"
                              max="2"
                              value={editedTemplate?.[key] || 1}
                              onChange={(e) => updateEditedTemplate(key, parseFloat(e.target.value))}
                              disabled={!editMode}
                              className="mt-1"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Body Font</Label>
                          <Select
                            value={editedTemplate?.body_font || 'Arial'}
                            onValueChange={(v) => updateEditedTemplate('body_font', v)}
                            disabled={!editMode}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Arial">Arial</SelectItem>
                              <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                              <SelectItem value="Calibri">Calibri</SelectItem>
                              <SelectItem value="Georgia">Georgia</SelectItem>
                              <SelectItem value="Verdana">Verdana</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Body Font Size</Label>
                          <Input
                            type="number"
                            min="8"
                            max="14"
                            value={editedTemplate?.body_font_size || 11}
                            onChange={(e) => updateEditedTemplate('body_font_size', parseInt(e.target.value))}
                            disabled={!editMode}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Select a template to view or edit</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
